import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ordersAPI, paymentsAPI } from '@/utils/api';
import { toast } from 'sonner';
import { Zap, ArrowLeft, CreditCard } from 'lucide-react';

const Checkout = () => {
  const navigate = useNavigate();
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    country: 'USA'
  });

  useEffect(() => {
    const savedCart = JSON.parse(localStorage.getItem('cart') || '[]');
    if (savedCart.length === 0) {
      navigate('/cart');
    }
    setCart(savedCart);
  }, [navigate]);

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const subtotal = cart.reduce((sum, item) => sum + item.current_price * item.quantity, 0);
  const memberDiscount = user.membership_type === 'premium' ? subtotal * 0.1 : 0;
  const total = subtotal - memberDiscount;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Create order
      const orderItems = cart.map(item => ({
        product_id: item.id,
        brand_id: item.brand_id,
        name: item.name,
        price: item.current_price,
        quantity: item.quantity,
        category: item.category
      }));

      const orderResponse = await ordersAPI.create({
        items: orderItems,
        shipping_address: formData
      });

      const orderId = orderResponse.data.order_id;

      // Create Stripe checkout session
      const checkoutResponse = await paymentsAPI.createCheckout({
        order_id: orderId,
        origin_url: window.location.origin
      });

      // Redirect to Stripe
      window.location.href = checkoutResponse.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Checkout failed');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0f0f10]">
      {/* Navbar */}
      <nav className="border-b border-white/10 bg-black/40 backdrop-blur-md">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center space-x-2 cursor-pointer" onClick={() => navigate('/')}>
            <Zap className="h-8 w-8 text-yellow-400" />
            <span className="text-2xl font-bold bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent">
              LuxLyx
            </span>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-6 py-12">
        <Button
          data-testid="back-btn"
          variant="ghost"
          onClick={() => navigate('/cart')}
          className="mb-8 text-gray-400 hover:text-white"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Cart
        </Button>

        <h1 className="text-4xl font-bold mb-8">Checkout</h1>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Shipping Form */}
          <div className="lg:col-span-2">
            <div className="bg-gradient-to-br from-[#1a1a1c] to-[#0f0f10] border border-white/10 rounded-2xl p-8">
              <h2 className="text-2xl font-bold mb-6">Shipping Address</h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Label htmlFor="name" className="text-gray-300">Full Name</Label>
                  <Input
                    id="name"
                    data-testid="name-input"
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="mt-2 bg-black/40 border-white/10 text-white"
                  />
                </div>
                
                <div>
                  <Label htmlFor="address" className="text-gray-300">Street Address</Label>
                  <Input
                    id="address"
                    data-testid="address-input"
                    type="text"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    required
                    className="mt-2 bg-black/40 border-white/10 text-white"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="city" className="text-gray-300">City</Label>
                    <Input
                      id="city"
                      data-testid="city-input"
                      type="text"
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      required
                      className="mt-2 bg-black/40 border-white/10 text-white"
                    />
                  </div>
                  <div>
                    <Label htmlFor="state" className="text-gray-300">State</Label>
                    <Input
                      id="state"
                      data-testid="state-input"
                      type="text"
                      value={formData.state}
                      onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                      required
                      className="mt-2 bg-black/40 border-white/10 text-white"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="zip" className="text-gray-300">ZIP Code</Label>
                    <Input
                      id="zip"
                      data-testid="zip-input"
                      type="text"
                      value={formData.zip}
                      onChange={(e) => setFormData({ ...formData, zip: e.target.value })}
                      required
                      className="mt-2 bg-black/40 border-white/10 text-white"
                    />
                  </div>
                  <div>
                    <Label htmlFor="country" className="text-gray-300">Country</Label>
                    <Input
                      id="country"
                      data-testid="country-input"
                      type="text"
                      value={formData.country}
                      onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                      required
                      className="mt-2 bg-black/40 border-white/10 text-white"
                    />
                  </div>
                </div>

                <Button
                  data-testid="payment-btn"
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-yellow-400 to-orange-500 text-black font-bold text-lg py-6 hover:opacity-90"
                >
                  <CreditCard className="mr-2 h-6 w-6" />
                  {loading ? 'Processing...' : 'Proceed to Payment'}
                </Button>
              </form>
            </div>
          </div>

          {/* Order Summary */}
          <div>
            <div className="bg-gradient-to-br from-[#1a1a1c] to-[#0f0f10] border border-white/10 rounded-2xl p-6 sticky top-6">
              <h2 className="text-2xl font-bold mb-6">Order Summary</h2>
              <div className="space-y-4 mb-6">
                {cart.map((item, index) => (
                  <div key={index} className="flex justify-between text-sm">
                    <span className="text-gray-300">
                      {item.name} x{item.quantity}
                    </span>
                    <span className="font-semibold">${(item.current_price * item.quantity).toFixed(2)}</span>
                  </div>
                ))}
              </div>
              <div className="space-y-3 border-t border-white/10 pt-4">
                <div className="flex justify-between text-gray-300">
                  <span>Subtotal</span>
                  <span className="font-semibold">${subtotal.toFixed(2)}</span>
                </div>
                {memberDiscount > 0 && (
                  <div className="flex justify-between text-yellow-400">
                    <span>Premium Discount (10%)</span>
                    <span className="font-semibold">-${memberDiscount.toFixed(2)}</span>
                  </div>
                )}
                <div className="border-t border-white/10 pt-3 flex justify-between text-xl font-bold">
                  <span>Total</span>
                  <span className="text-yellow-400">${total.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
